import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import AssetProfileFormFields from '@/components/assets/AssetProfileFormFields';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  attachPortfolioLinks,
  attachCompanyLinks,
  createEmptyAssetForm,
  handleCompanySelection,
  handlePortfolioSelection,
  handleSubPortfolioSelection,
  handleTechnologyChange,
  normalizeAsset,
  operationalStatusOptions,
  prepareAssetPayload,
  validateAssetForm,
} from '@/lib/assets';
import {
  ChevronRight,
  ChevronDown,
  Search,
  Filter,
  Wind,
  Sun,
  Battery,
  MapPin,
  Activity,
  FolderKanban,
  Bolt,
  CalendarDays,
  UserRound,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const assetIcons = {
  wind: Wind,
  solar: Sun,
  battery: Battery,
};

const operationalStatusColors = {
  operational: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  warning: 'bg-amber-100 text-amber-700 border-amber-300',
  critical: 'bg-red-100 text-red-700 border-red-300',
  offline: 'bg-slate-100 text-slate-600 border-slate-300',
};

const lifecycleStatusColors = {
  operational: 'bg-sky-100 text-sky-700 border-sky-300',
  cod_achieved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  performance_testing: 'bg-violet-100 text-violet-700 border-violet-300',
  grid_connection_in_progress: 'bg-amber-100 text-amber-700 border-amber-300',
  under_construction: 'bg-orange-100 text-orange-700 border-orange-300',
  under_development: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  planned_initiative: 'bg-slate-100 text-slate-600 border-slate-300',
};

const internalRoles = ['superadmin', 'platform_admin', 'org_admin', 'org_user'];

function formatCapacity(value) {
  const parsed = Number(value || 0);
  return `${parsed.toLocaleString()} kWp`;
}

function formatLifecycle(value) {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatDate(value) {
  return value || 'Not set';
}

export default function AssetRegistryPage() {
  const [assets, setAssets] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [subPortfolios, setSubPortfolios] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedSubPortfolios, setExpandedSubPortfolios] = useState({});
  const [expandedPortfolios, setExpandedPortfolios] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(createEmptyAssetForm());

  useEffect(() => {
    fetchAssets();
  }, []);

  const handleCreateChange = (key, value) => {
    setCreateForm((state) => ({ ...state, [key]: value }));
  };

  const handleCreateTechnologyChange = (technology) => {
    setCreateForm((state) => handleTechnologyChange(state, technology));
  };

  const handleCreateAssetManagerChange = (userId) => {
    const selectedUser = internalUsers.find((user) => user.id === userId);
    setCreateForm((state) => ({
      ...state,
      asset_manager_id: userId,
      asset_manager_name: selectedUser?.name || '',
    }));
  };

  const handleCreatePortfolioChange = (portfolioId) => {
    setCreateForm((state) => handlePortfolioSelection(state, portfolioId, portfolios, subPortfolios));
  };

  const handleCreateSubPortfolioChange = (subPortfolioId) => {
    setCreateForm((state) => handleSubPortfolioSelection(state, subPortfolioId, subPortfolios));
  };

  const handleCreateCompanyChange = (sectionKey, companyId) => {
    setCreateForm((state) => handleCompanySelection(state, sectionKey, companyId, companies));
  };

  const fetchAssets = async () => {
    try {
      const [
        assetsResponse,
        usersResponse,
        portfoliosResponse,
        subPortfoliosResponse,
        companiesResponse,
      ] = await Promise.all([
        axios.get(`${API}/assets`),
        axios.get(`${API}/users`),
        axios.get(`${API}/portfolios`),
        axios.get(`${API}/sub-portfolios`),
        axios.get(`${API}/companies`),
      ]);
      const enrichedAssets = assetsResponse.data.map((asset) =>
        attachCompanyLinks(
          attachPortfolioLinks(asset, portfoliosResponse.data, subPortfoliosResponse.data),
          companiesResponse.data
        )
      );
      const users = usersResponse.data.filter(
        (user) => internalRoles.includes(user.role) && user.status === 'active'
      );

      setAssets(enrichedAssets);
      setInternalUsers(users);
      setPortfolios(portfoliosResponse.data);
      setSubPortfolios(subPortfoliosResponse.data);
      setCompanies(companiesResponse.data);

      const initialSubPortfolios = [...new Set(enrichedAssets.map((asset) => asset.sub_portfolio).filter(Boolean))];
      if (initialSubPortfolios.length > 0) {
        setExpandedSubPortfolios({ [initialSubPortfolios[0]]: true });
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitCreate = async () => {
    const missingFields = validateAssetForm(createForm);
    if (missingFields.length > 0) {
      toast.error(`Please complete: ${missingFields.join(', ')}`);
      return;
    }

    try {
      const payload = prepareAssetPayload(createForm, internalUsers);
      await axios.post(`${API}/assets`, payload);
      setShowCreate(false);
      setCreateForm(createEmptyAssetForm());
      fetchAssets();
    } catch (error) {
      console.error('Failed to create asset', error);
      toast.error('Failed to create asset');
    }
  };

  const filteredAssets = assets.filter((asset) => {
    const normalizedAsset = normalizeAsset(asset);
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      searchQuery === '' ||
      normalizedAsset.name.toLowerCase().includes(query) ||
      normalizedAsset.asset_id.toLowerCase().includes(query) ||
      normalizedAsset.portfolio.toLowerCase().includes(query) ||
      normalizedAsset.sub_portfolio.toLowerCase().includes(query);
    const matchesStatus =
      statusFilter === 'all' || normalizedAsset.operational_status === statusFilter;
    const matchesType = typeFilter === 'all' || normalizedAsset.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const groupedAssets = filteredAssets.reduce((acc, asset) => {
    const normalizedAsset = normalizeAsset(asset);
    const subPortfolio = normalizedAsset.sub_portfolio || 'Unassigned Sub-portfolio';
    const portfolio = normalizedAsset.portfolio || 'Unassigned Portfolio';
    if (!acc[subPortfolio]) {
      acc[subPortfolio] = {};
    }
    if (!acc[subPortfolio][portfolio]) {
      acc[subPortfolio][portfolio] = [];
    }
    acc[subPortfolio][portfolio].push(normalizedAsset);
    return acc;
  }, {});

  const getSubPortfolioStats = (portfolioMap) => {
    const allAssets = Object.values(portfolioMap).flat();
    return {
      total: allAssets.length,
      operational: allAssets.filter((asset) => asset.operational_status === 'operational').length,
      totalCapacity: allAssets.reduce(
        (sum, asset) => sum + Number(asset.dc_installed_capacity_kwp || 0),
        0
      ),
    };
  };

  const toggleSubPortfolio = (subPortfolio) => {
    setExpandedSubPortfolios((state) => ({ ...state, [subPortfolio]: !state[subPortfolio] }));
  };

  const togglePortfolio = (portfolio) => {
    setExpandedPortfolios((state) => ({ ...state, [portfolio]: !state[portfolio] }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Asset Registry</h1>
            <p className="text-slate-500">
              {filteredAssets.length} assets across {Object.keys(groupedAssets).length} sub-portfolios
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {operationalStatusOptions.slice(0, 3).map((status) => (
              <div
                key={status.value}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg',
                  status.value === 'operational'
                    ? 'bg-emerald-50'
                    : status.value === 'warning'
                      ? 'bg-amber-50'
                      : 'bg-red-50'
                )}
              >
                <div
                  className={cn(
                    'w-2 h-2 rounded-full',
                    status.value === 'operational'
                      ? 'bg-emerald-500'
                      : status.value === 'warning'
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                  )}
                />
                <span
                  className={cn(
                    'text-sm font-medium',
                    status.value === 'operational'
                      ? 'text-emerald-700'
                      : status.value === 'warning'
                        ? 'text-amber-700'
                        : 'text-red-700'
                  )}
                >
                  {assets.filter((asset) => normalizeAsset(asset).operational_status === status.value).length}{' '}
                  {status.label}
                </span>
              </div>
            ))}
            <Button
              onClick={() => {
                setCreateForm(createEmptyAssetForm());
                setShowCreate(true);
              }}
              data-testid="add-asset"
            >
              Add Asset
            </Button>
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by asset name, ID, portfolio, or sub-portfolio..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="asset-search"
                />
              </div>
              <div className="flex gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[190px]" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Operational Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Operational Status</SelectItem>
                    {operationalStatusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="type-filter">
                    <SelectValue placeholder="Technology" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Technologies</SelectItem>
                    <SelectItem value="solar">Solar</SelectItem>
                    <SelectItem value="wind">Wind</SelectItem>
                    <SelectItem value="battery">BESS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {showCreate && (
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-slate-900">Add Asset</h2>
                  <p className="text-sm text-slate-500">
                    Define the commercial profile here. Operational health metrics stay in analytics and dashboards.
                  </p>
                </div>
              </div>
              <AssetProfileFormFields
                form={createForm}
                users={internalUsers}
                portfolios={portfolios}
                subPortfolios={subPortfolios}
                companies={companies}
                onChange={handleCreateChange}
                onTechnologyChange={handleCreateTechnologyChange}
                onAssetManagerChange={handleCreateAssetManagerChange}
                onPortfolioChange={handleCreatePortfolioChange}
                onSubPortfolioChange={handleCreateSubPortfolioChange}
                onCompanyChange={handleCreateCompanyChange}
              />
              <div className="flex gap-2 mt-6">
                <Button onClick={submitCreate}>Create Asset</Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreate(false);
                    setCreateForm(createEmptyAssetForm());
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200" data-testid="asset-tree">
          <CardContent className="p-0">
            {Object.keys(groupedAssets).length === 0 ? (
              <EmptyState
                icon={Activity}
                title="No assets found"
                description={searchQuery ? 'Try adjusting your search or filters' : 'Add your first asset to get started'}
                actionLabel={!searchQuery ? 'Add Asset' : undefined}
                onAction={!searchQuery ? () => setShowCreate(true) : undefined}
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {Object.entries(groupedAssets).map(([subPortfolio, portfolios]) => {
                  const subPortfolioStats = getSubPortfolioStats(portfolios);
                  const isExpanded = expandedSubPortfolios[subPortfolio];

                  return (
                    <div key={subPortfolio}>
                      <div
                        className="flex items-center gap-3 p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => toggleSubPortfolio(subPortfolio)}
                        data-testid={`sub-portfolio-${subPortfolio}`}
                      >
                        <button className="p-1">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                        </button>
                        <MapPin className="w-5 h-5 text-slate-400" />
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{subPortfolio}</p>
                          <p className="text-sm text-slate-500">
                            {subPortfolioStats.total} assets • {subPortfolioStats.operational} operational
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Installed DC Capacity</p>
                          <p className="font-mono font-medium text-slate-900">
                            {formatCapacity(subPortfolioStats.totalCapacity)}
                          </p>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="bg-slate-50/50">
                          {Object.entries(portfolios).map(([portfolio, portfolioAssets]) => {
                            const isPortfolioExpanded = expandedPortfolios[portfolio] !== false;

                            return (
                              <div key={portfolio} className="border-t border-slate-100">
                                <div
                                  className="flex items-center gap-3 p-3 pl-12 hover:bg-slate-100/50 cursor-pointer transition-colors"
                                  onClick={() => togglePortfolio(portfolio)}
                                  data-testid={`portfolio-${portfolio}`}
                                >
                                  <button className="p-1">
                                    {isPortfolioExpanded ? (
                                      <ChevronDown className="w-4 h-4 text-slate-400" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-slate-400" />
                                    )}
                                  </button>
                                  <FolderKanban className="w-4 h-4 text-slate-400" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-700">{portfolio}</p>
                                    <p className="text-xs text-slate-500">
                                      {portfolioAssets.length} assets •{' '}
                                      {formatCapacity(
                                        portfolioAssets.reduce(
                                          (sum, asset) => sum + Number(asset.dc_installed_capacity_kwp || 0),
                                          0
                                        )
                                      )}
                                    </p>
                                  </div>
                                </div>

                                {isPortfolioExpanded && (
                                  <div className="divide-y divide-slate-100">
                                    {portfolioAssets.map((asset) => {
                                      const Icon = assetIcons[asset.type] || Activity;

                                      return (
                                        <Link
                                          key={asset.id}
                                          to={`/assets/${asset.id}`}
                                          className="flex items-center gap-4 p-3 pl-20 hover:bg-white transition-colors"
                                          data-testid={`asset-${asset.asset_id}`}
                                        >
                                          <div
                                            className={cn(
                                              'w-8 h-8 rounded-lg flex items-center justify-center',
                                              asset.type === 'wind'
                                                ? 'bg-sky-100'
                                                : asset.type === 'solar'
                                                  ? 'bg-amber-100'
                                                  : 'bg-emerald-100'
                                            )}
                                          >
                                            <Icon
                                              className={cn(
                                                'w-4 h-4',
                                                asset.type === 'wind'
                                                  ? 'text-sky-600'
                                                  : asset.type === 'solar'
                                                    ? 'text-amber-600'
                                                    : 'text-emerald-600'
                                              )}
                                            />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="font-mono text-sm font-medium text-slate-900">
                                                {asset.asset_id}
                                              </span>
                                              <Badge
                                                variant="outline"
                                                className={cn(
                                                  'text-xs',
                                                  operationalStatusColors[asset.operational_status]
                                                )}
                                              >
                                                {asset.operational_status}
                                              </Badge>
                                              <Badge
                                                variant="outline"
                                                className={cn(
                                                  'text-xs',
                                                  lifecycleStatusColors[asset.lifecycle_status]
                                                )}
                                              >
                                                {formatLifecycle(asset.lifecycle_status)}
                                              </Badge>
                                            </div>
                                            <p className="text-sm text-slate-500 truncate">{asset.name}</p>
                                          </div>
                                          <div className="hidden xl:flex items-center gap-6">
                                            <div className="text-center">
                                              <p className="text-xs text-slate-500">Asset Type</p>
                                              <p className="text-sm font-medium capitalize">
                                                {formatLifecycle(asset.asset_type)}
                                              </p>
                                            </div>
                                            <div className="text-center">
                                              <p className="text-xs text-slate-500">DC Capacity</p>
                                              <p className="font-mono text-sm font-medium">
                                                {formatCapacity(asset.dc_installed_capacity_kwp)}
                                              </p>
                                            </div>
                                            <div className="text-center">
                                              <p className="text-xs text-slate-500">Asset Manager</p>
                                              <p className="text-sm flex items-center gap-1">
                                                <UserRound className="w-3 h-3" />
                                                {asset.asset_manager_name || 'Unassigned'}
                                              </p>
                                            </div>
                                            <div className="text-center">
                                              <p className="text-xs text-slate-500">Commercial Operation</p>
                                              <p className="text-sm flex items-center gap-1">
                                                <CalendarDays className="w-3 h-3" />
                                                {formatDate(asset.commercial_operation_date)}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="hidden md:flex items-center gap-2 text-slate-500">
                                            <Bolt className="w-4 h-4" />
                                            <span className="text-sm font-medium">
                                              {formatCapacity(asset.dc_installed_capacity_kwp)}
                                            </span>
                                          </div>
                                          <ChevronRight className="w-4 h-4 text-slate-400" />
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
